import { ExtendedWarrantyLineConfig, ExtendedWarrantyOutput } from '../types';
import { Quote, QuoteLine } from '../entities/Quote';
import { calculatePrice } from './priceHandler';
import { mergeConfigState } from './mergeHandler';

const getMaximumWarrantyDurationOption = (warranty: ExtendedWarrantyLineConfig) => {
  return warranty.warrantyOptions.find(
    (option) => option.duration === warranty.warrantyMaxExtDuration
  )!;
};

const getWarrantyLine = (quote: Quote) => {
  return quote.items.find((line) => line.modelName === 'Warranty');
};

const getWarrantyInitialState = (currency: string, warrantyInput: ExtendedWarrantyLineConfig[]) => {
  return warrantyInput.reduce((state, warranty) => {
    // Always initiate the state with maximum ext. warranty selected
    const { duration, price } = getMaximumWarrantyDurationOption(warranty);

    return {
      ...state,
      [warranty.warrantedPartNumber]: {
        currency,
        warrantedPartNumber: warranty.warrantedPartNumber,
        warrantedQuantity: warranty.warrantedQuantity,
        warrantyPartNumber: warranty.warrantyPartNumber,
        warrantyDuration: duration,
        warrantyPrice: calculatePrice({
          warrantedQuantity: warranty.warrantedQuantity,
          warrantyDuration: duration,
          warrantyPrice: price,
        }),
      },
    } as ExtendedWarrantyOutput;
  }, {} as ExtendedWarrantyOutput);
};

const readWarrantyLinesFromQuote = (quote: Quote, warrantyHeaderLineNumber: number) => {
  return quote.items.filter(
    (line) => parseInt(line.parentDocumentNumber || '') === warrantyHeaderLineNumber
  );
};

const getWarrantyConfigStateFromLines = (
  quote: Quote,
  warrantyLinesFromQuote: QuoteLine[]
): ExtendedWarrantyOutput => {
  return warrantyLinesFromQuote.reduce((state, line) => {
    // It shouldn't happen, but it's a defensive check.
    // If the line is in the quote but NOT in the warranty input, then we can ignore it
    const lineOnInput = quote.warrantyInput.find(
      (warranty) => warranty.warrantedPartNumber === line.warrantyForPartNumber
    );

    if (!lineOnInput) {
      return state;
    }

    let { duration, price } = getMaximumWarrantyDurationOption(lineOnInput);

    // When a warranty line is marked optional, this is because the initial
    // selection was "None"
    if (line.optional) {
      duration = 0;
      price = 0;
    } else {
      // We have to check if the previously selected subscriptionDuration is still
      // in the list of valid durations. If it isn't, we're going to default to duration: 0
      const durationOption = lineOnInput.warrantyOptions.find(
        (option) => option.duration === line.subscriptionDuration
      );

      if (durationOption) {
        duration = line.subscriptionDuration!;

        // Recalculate price to make sure we open the configurator will a matching price
        price = calculatePrice({
          warrantedQuantity: line.extendedWarrantyQuantity!,
          warrantyDuration: line.subscriptionDuration!,
          warrantyPrice: durationOption.price,
        });
      }
    }

    return {
      ...state,
      [line.warrantyForPartNumber!]: {
        currency: quote.currencyCode,
        warrantedPartNumber: line.warrantyForPartNumber,
        warrantedQuantity: line.extendedWarrantyQuantity,
        warrantyPartNumber: line.partNumber,
        warrantyDuration: duration,
        warrantyPrice: price,
      },
    } as ExtendedWarrantyOutput;
  }, {} as ExtendedWarrantyOutput);
};

export const getWarrantyConfigStateFromQuote = (quote: Quote) => {
  const warrantyLine = getWarrantyLine(quote);

  // If we don't have any warranty line, we'll initialize the state with duration: 0
  if (!warrantyLine) {
    return getWarrantyInitialState(quote.currencyCode, quote.warrantyInput);
  }

  const warrantyLinesFromQuote = readWarrantyLinesFromQuote(quote, warrantyLine.documentNumber!);

  const configStateFromLines = getWarrantyConfigStateFromLines(quote, warrantyLinesFromQuote);

  // Ensure the state is merged to guarantee we are aligned with the quote lines
  return mergeConfigState(quote.warrantyInput, configStateFromLines, quote.currencyCode);
};
